import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import RouteSkeleton from "@/components/RouteSkeleton";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import SkillControlsPanel from "../SkillControlsPanel";
import IntegrationActivityLog from "../IntegrationActivityLog";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import {
  SectionCard,
  Divider,
  FieldInput,
  PrimaryButton,
  GhostButton
} from "@/components/ds";
import {
  Lightbulb,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Check
} from "@/components/ds/icons";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface KlaviyoStatus {
  connected: boolean;
  apiKeyMasked: string;
  accountName: string;
  eventCount: number;
  lastEventAt: string | null;
}

interface KlaviyoEventTemplate {
  id: string;
  name: string;
  trigger: string;
  description: string;
}

interface EventSetting {
  eventId: string;
  isActive: boolean;
  flowId: string;
}

// ─── Suggested events (5 templates from API, also matches server-side) ─────────

const DEFAULT_EVENTS: KlaviyoEventTemplate[] = [
  {
    id: "post_chat",
    name: "Post Chat Follow-up",
    trigger: "conversation_resolved",
    description:
      "Send a follow-up email after every resolved chat. Great for NPS, CSAT, and product recommendations."
  },
  {
    id: "csat_survey",
    name: "CSAT Survey",
    trigger: "conversation_resolved_csat",
    description:
      "Only fires when no CSAT score was collected in-chat. Sends a 1-question email survey."
  },
  {
    id: "abandoned_chat",
    name: "Abandoned Chat Recovery",
    trigger: "conversation_abandoned",
    description:
      "Fires when visitor leaves without getting an answer. Offers to pick up where they left off."
  },
  {
    id: "bot_escalation",
    name: "Escalation Notification",
    trigger: "bot_escalated_to_human",
    description:
      "When the bot can't answer and escalates to a human — send a 'we got your request' holding email."
  },
  {
    id: "order_followup",
    name: "Post-Purchase Bot Flow",
    trigger: "shopify_order_confirmed",
    description:
      "After a Shopify order closes in chat — trigger the post-purchase Klaviyo flow."
  }
];

// ─── AI Skills ─────────────────────────────────────────────────────────────────

const KLAVIYO_AI_TOOLS = [
  {
    name: "klaviyo_get_lists",
    label: "Get Lists",
    group: "Lists",
    description:
      "Discover all Klaviyo lists and their IDs. Use before subscribing a customer when the list ID is unknown."
  },
  {
    name: "klaviyo_add_to_list",
    label: "Add to List",
    group: "Lists",
    description:
      "Subscribe a customer email to a Klaviyo list in real time. Creates or upserts the profile automatically."
  },
  {
    name: "klaviyo_remove_from_list",
    label: "Remove from List",
    group: "Lists",
    description:
      "Unsubscribe a customer from a specific list. Use for compliance requests or preference changes."
  },
  {
    name: "klaviyo_track_event",
    label: "Track Event",
    group: "Events",
    description:
      "Fire a custom Klaviyo event (e.g. 'High Purchase Intent') — appears in flows and analytics within seconds."
  },
  {
    name: "klaviyo_get_profile",
    label: "Get Profile",
    group: "Profiles",
    description:
      "Look up a subscriber's Klaviyo profile by email — list memberships, custom attributes, and created date."
  },
  {
    name: "klaviyo_update_profile",
    label: "Update Profile",
    group: "Profiles",
    description:
      "Set custom attributes mid-chat (vip_tier, product_interest, chat_source) on a subscriber's profile."
  }
];

// ─── Helper: chevron icon ────────────────────────────────────────────────────────

function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronDown
      className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
    />
  );
}

// ─── FindKeyAccordion ────────────────────────────────────────────────────────────

function FindKeyAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-zinc-50 hover:bg-zinc-100 text-sm font-medium text-zinc-700"
        aria-expanded={open}
      >
        <span>Where to find your Private API Key</span>
        <Chevron open={open} />
      </button>
      {open && (
        <>
          <Divider className="my-0" />
          <div className="px-4 pb-4 pt-3 text-sm text-zinc-600 space-y-1.5 bg-white">
            <ol className="list-none space-y-2">
              {[
                "Log into your Klaviyo account at app.klaviyo.com",
                "Click your account name (top-left) → Settings → API Keys",
                'Click "Create Private API Key"',
                'Name it "Velaro Integration"',
                "Set Permissions to Full Access",
                "Click Create → Copy the key"
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-(--ds-primary) text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <a
              href="https://developers.klaviyo.com/en/reference/api_overview"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs mt-2"
              aria-label="Klaviyo API documentation (opens in new tab)"
            >
              Klaviyo API docs
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </div>
        </>
      )}
    </div>
  );
}

// ─── FlowTipAccordion ────────────────────────────────────────────────────────────

function FlowTipAccordion({ eventName }: { eventName: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-100 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-zinc-50 hover:bg-zinc-100 text-xs font-medium text-zinc-600"
        aria-expanded={open}
      >
        <span>How to connect this to a Klaviyo Flow</span>
        <Chevron open={open} />
      </button>
      {open && (
        <>
          <Divider className="my-0" />
          <div className="px-3 pb-3 pt-2.5 text-xs text-zinc-600 space-y-1.5 bg-white">
            <ol className="list-none space-y-1.5">
              {[
                "In Klaviyo, go to Flows → Create Flow",
                'Choose "Build your own"',
                'Set the trigger to "API" (Metric trigger)',
                `Use the metric name "Velaro ${eventName}"`,
                "Build your email sequence (e.g. send email 1 hour after trigger)",
                "Publish the flow",
                "Copy the Flow ID from the URL (e.g. ...flows/ABCD1234/...) and paste it in the field above"
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="shrink-0 w-4 h-4 rounded-full bg-zinc-200 text-zinc-600 text-[9px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

// ─── EventCard ────────────────────────────────────────────────────────────────

interface EventCardProps {
  template: KlaviyoEventTemplate;
  setting: EventSetting;
  connected: boolean;
  onChange: (updated: EventSetting) => void;
}

function EventCard({ template, setting, connected, onChange }: EventCardProps) {
  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        setting.isActive
          ? "border-blue-200 bg-blue-50/30"
          : "border-zinc-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-zinc-800">
              {template.name}
            </span>
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500 font-mono">
              {template.trigger}
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">{template.description}</p>
        </div>

        {/* Toggle — bespoke aria-label (shared <Toggle> has no aria-label passthrough) */}
        <button
          type="button"
          role="switch"
          aria-checked={setting.isActive}
          aria-label={`${setting.isActive ? "Disable" : "Enable"} ${template.name}`}
          disabled={!connected}
          onClick={() => onChange({ ...setting, isActive: !setting.isActive })}
          className={`relative shrink-0 w-10 h-5 rounded-full transition-colors ${
            setting.isActive ? "bg-blue-600" : "bg-zinc-300"
          } ${!connected ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              setting.isActive ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {/* Flow ID input — only shown when active */}
      {setting.isActive && (
        <div className="mt-3">
          <label
            htmlFor={`flow-${template.id}`}
            className="block text-xs font-medium text-zinc-600 mb-1"
          >
            Klaviyo Flow ID
            <span className="text-zinc-400 font-normal ml-1">
              (paste from your Klaviyo flow URL)
            </span>
          </label>
          <FieldInput
            id={`flow-${template.id}`}
            type="text"
            value={setting.flowId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ ...setting, flowId: e.target.value })
            }
            placeholder="e.g. ABCD1234"
          />
          <FlowTipAccordion eventName={template.name} />
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Klaviyo() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<KlaviyoStatus | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [saveResult, setSaveResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [eventSettings, setEventSettings] = useState<EventSetting[]>([]);
  const [tab, setTab] = useState<"overview" | "config" | "skills" | "log">(
    "config"
  );

  // ── Load status + event settings ───────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusResp = await api.admin.get("Klaviyo/Status");
      const statusData: KlaviyoStatus = await statusResp.json();
      setStatus(
        statusData ?? {
          connected: false,
          apiKeyMasked: "",
          accountName: "",
          eventCount: 0,
          lastEventAt: null
        }
      );

      const settingsResp = await api.admin.get("Klaviyo/EventSettings");
      const settingsData = await settingsResp.json().catch(() => []);
      const savedSettings: EventSetting[] = Array.isArray(settingsData)
        ? settingsData
        : [];

      // Merge saved settings with defaults (fill in any missing event ids)
      const merged = DEFAULT_EVENTS.map((tmpl) => {
        const saved = savedSettings.find((s) => s.eventId === tmpl.id);
        return saved ?? { eventId: tmpl.id, isActive: false, flowId: "" };
      });
      setEventSettings(merged);
    } catch {
      setStatus({
        connected: false,
        apiKeyMasked: "",
        accountName: "",
        eventCount: 0,
        lastEventAt: null
      });
      setEventSettings(
        DEFAULT_EVENTS.map((t) => ({
          eventId: t.id,
          isActive: false,
          flowId: ""
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Save & Test ─────────────────────────────────────────────────────────────

  async function handleSaveAndTest() {
    setSaving(true);
    setTestResult(null);
    setSaveResult(null);
    setDisconnectError(null);
    try {
      // 1. Save config (key + event settings)
      const saveResp = await api.admin.post("Klaviyo/SaveConfig", {
        privateApiKey: apiKey.trim() || undefined,
        eventSettings: eventSettings
      });
      const saveData = await saveResp.json();

      if (!saveData?.success) {
        setSaveResult({
          success: false,
          message: saveData?.error ?? "Failed to save."
        });
        return;
      }
      setSaveResult({ success: true, message: "Configuration saved." });

      // 2. Test connection
      setTesting(true);
      const testResp = await api.admin.post("Klaviyo/TestConnection", {
        privateApiKey: apiKey.trim() || undefined
      });
      const testData = await testResp.json();

      if (testData?.success) {
        setTestResult({
          success: true,
          message: `Connected${testData.accountName ? ` — ${testData.accountName}` : ""}.`
        });
        setApiKey(""); // clear the input — masked key will show from status
        await load();
      } else {
        setTestResult({
          success: false,
          message:
            testData?.error ?? "Connection test failed. Check your API key."
        });
      }
    } catch {
      setSaveResult({
        success: false,
        message: "Network error — check your connection and try again."
      });
    } finally {
      setSaving(false);
      setTesting(false);
    }
  }

  async function handleSaveEvents() {
    setSaving(true);
    setSaveResult(null);
    try {
      const resp = await api.admin.post("Klaviyo/SaveConfig", {
        eventSettings: eventSettings
      });
      const data = await resp.json();
      setSaveResult({
        success: data?.success ?? false,
        message: data?.success
          ? "Event settings saved."
          : (data?.error ?? "Failed to save.")
      });
    } catch {
      setSaveResult({ success: false, message: "Network error." });
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect() {
    if (
      !confirm(
        "Disconnect Klaviyo? Event flows and AI skills will stop working until reconnected."
      )
    )
      return;
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      const resp = await api.admin.delete("Klaviyo/Config");
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        setDisconnectError(
          `Failed to disconnect (HTTP ${resp.status}). ${body}`.trim()
        );
        return;
      }
      setApiKey("");
      setSaveResult(null);
      setTestResult(null);
      await load();
    } catch (err) {
      setDisconnectError(
        `Failed to disconnect: ${err instanceof Response ? err.status : String(err)}`
      );
    } finally {
      setDisconnecting(false);
    }
  }

  function handleEventChange(updated: EventSetting) {
    setEventSettings((prev) =>
      prev.map((s) => (s.eventId === updated.eventId ? updated : s))
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return <RouteSkeleton />;
  }

  const connected = status?.connected ?? false;

  const tabsJSX = (
    <>
      <IntegrationTab
        active={tab === "overview"}
        onClick={() => setTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={tab === "config"}
        onClick={() => setTab("config")}
      >
        Configuration
      </IntegrationTab>
      <IntegrationTab
        active={tab === "skills"}
        onClick={() => setTab("skills")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab active={tab === "log"} onClick={() => setTab("log")}>
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Klaviyo" tabs={tabsJSX}>
      {/* ── Overview Tab ──────────────────────────────────────────────────── */}
      {tab === "overview" && (
        <>
          {/* ── Section: How it works ────────────────────────────────────────── */}
          <SectionCard title="How it works">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {[
                {
                  num: "1",
                  label: "Visitor chats with your bot",
                  icon: "MessageSquare"
                },
                {
                  num: "2",
                  label:
                    "Conversation resolves → Velaro fires a Klaviyo event with visitor email + conversation summary",
                  icon: "Zap"
                },
                {
                  num: "3",
                  label:
                    "Klaviyo sends your pre-built email flow automatically",
                  icon: "Mail"
                }
              ].map((step, i) => (
                <React.Fragment key={step.num}>
                  <div className="flex items-start gap-3 flex-1">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-(--ds-primary) text-white text-sm font-bold flex items-center justify-center">
                      {step.num}
                    </span>
                    <div>
                      <IconByName name={step.icon} className="w-4 h-4" />
                      <p className="text-sm text-zinc-700 mt-0.5">
                        {step.label}
                      </p>
                    </div>
                  </div>
                  {i < 2 && (
                    <ChevronRight className="hidden sm:block shrink-0 w-5 h-5 text-zinc-300" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <Divider className="mt-5 mb-4" />
            <div className="rounded bg-zinc-50 p-3 text-xs text-zinc-600">
              <strong className="text-zinc-700">
                Velaro sends to Klaviyo:
              </strong>{" "}
              visitor email, first name, conversation summary, channel (chat /
              SMS / WhatsApp), CSAT score (if collected), and any conversation
              variables extracted during the chat.
            </div>
          </SectionCard>

          {/* ── Section: Workflow Templates ─────────────────────────────────── */}
          <SectionCard
            title="Workflow Templates"
            description="Launch a pre-built Klaviyo workflow in seconds — post-chat follow-ups, cart recovery, and more."
            action={<LaunchWizardButton onClick={() => setShowWizard(true)} />}
          >
            <></>
          </SectionCard>
        </>
      )}

      {/* ── Configuration Tab ─────────────────────────────────────────────── */}
      {tab === "config" && (
        <div className="space-y-5 max-w-2xl">
          {/* ── Section 1: Connect ─────────────────────────────────────────────── */}
          <SectionCard
            title="Connect Klaviyo"
            description="Trigger email and SMS flows after Velaro conversations resolve."
            action={
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full ${connected ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
              >
                {connected ? "Connected" : "Not connected"}
              </span>
            }
          >
            {/* Connected state: show account + masked key */}
            {connected && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-sm text-green-800 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-600 shrink-0">
                  <Check className="w-2.5 h-2.5 text-white" />
                </span>
                <span>
                  {status?.accountName ? (
                    <>
                      Connected to <strong>{status.accountName}</strong>
                    </>
                  ) : (
                    "Connected"
                  )}
                  {status?.apiKeyMasked && (
                    <span className="ml-2 text-green-700 font-mono text-xs">
                      {status.apiKeyMasked}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* API Key input */}
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="klaviyo-api-key"
                  className="block text-sm font-medium text-zinc-700 mb-1"
                >
                  Private API Key
                  {connected && (
                    <span className="text-zinc-400 font-normal ml-1">
                      (leave blank to keep current key)
                    </span>
                  )}
                </label>
                <FieldInput
                  id="klaviyo-api-key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setTestResult(null);
                    setSaveResult(null);
                    setDisconnectError(null);
                  }}
                  placeholder="pk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  autoComplete="new-password"
                  className="font-mono"
                />
                <FindKeyAccordion />
              </div>

              {/* Result banners */}
              {saveResult && (
                <div
                  className={`rounded-lg p-3 text-sm ${
                    saveResult.success
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-red-50 border border-red-200 text-red-800"
                  }`}
                  role="status"
                >
                  {saveResult.message}
                </div>
              )}
              {testResult && (
                <div
                  className={`rounded-lg p-3 text-sm ${
                    testResult.success
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-red-50 border border-red-200 text-red-800"
                  }`}
                  role="status"
                >
                  {testResult.message}
                </div>
              )}

              {disconnectError && (
                <div
                  className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800"
                  role="alert"
                >
                  {disconnectError}
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <PrimaryButton
                  onClick={handleSaveAndTest}
                  disabled={
                    saving ||
                    testing ||
                    disconnecting ||
                    (!apiKey && !connected)
                  }
                >
                  {saving
                    ? "Saving…"
                    : testing
                      ? "Testing…"
                      : "Save & Test Connection"}
                </PrimaryButton>
                {connected && (
                  <GhostButton
                    className="!text-zinc-400 hover:!text-red-600"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? "Disconnecting…" : "Disconnect"}
                  </GhostButton>
                )}
                <a
                  href="https://developers.klaviyo.com/en/reference/api_overview"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                  aria-label="Klaviyo developer documentation (opens in new tab)"
                >
                  Klaviyo docs
                </a>
              </div>
            </div>

            {/* Usage stats */}
            {connected && (status?.eventCount ?? 0) > 0 && (
              <>
                <Divider className="mt-4 mb-0" />
                <div className="pt-4 flex items-center gap-6 text-xs text-zinc-500">
                  <span>
                    <strong className="text-zinc-700">
                      {status?.eventCount?.toLocaleString()}
                    </strong>{" "}
                    events fired
                  </span>
                  {status?.lastEventAt && (
                    <span>
                      Last event:{" "}
                      <strong className="text-zinc-700">
                        {new Date(status.lastEventAt).toLocaleString()}
                      </strong>
                    </span>
                  )}
                </div>
              </>
            )}
          </SectionCard>

          {/* ── Deploy callout ── */}
          {connected && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-blue-800 mb-0.5">
                    Deploy the chat widget to your site
                  </div>
                  <div className="text-xs text-blue-600">
                    Your Klaviyo integration is connected — grab your embed code
                    from Deployments to go live.
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to="/chat_window"
                    className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 transition"
                  >
                    Chat Window
                  </Link>
                  <Link
                    to="/deployment"
                    className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition inline-flex items-center gap-1"
                  >
                    Get Embed Code
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
                <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Tip:</strong> You can also create a{" "}
                  <strong>Knowledge Base Q&A</strong> page widget from{" "}
                  <Link
                    to="/deployment"
                    className="underline hover:text-blue-900"
                  >
                    Deployments → Page Widgets
                  </Link>{" "}
                  for a lightweight, AI-only Q&A experience — no live agent
                  needed.
                </span>
              </div>
            </div>
          )}

          {/* ── Section 2: Events ──────────────────────────────────────────────── */}
          {connected && (
            <SectionCard
              title="Event Triggers"
              description="Choose which Velaro events fire Klaviyo flows. Toggle active and paste your Klaviyo Flow ID."
              action={
                <PrimaryButton
                  onClick={handleSaveEvents}
                  disabled={saving}
                  className="shrink-0"
                >
                  {saving ? "Saving…" : "Save Events"}
                </PrimaryButton>
              }
            >
              {saveResult && (
                <div
                  className={`rounded-lg p-3 text-sm mb-4 ${
                    saveResult.success
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-red-50 border border-red-200 text-red-800"
                  }`}
                  role="status"
                >
                  {saveResult.message}
                </div>
              )}

              <div className="space-y-3 mt-4">
                {DEFAULT_EVENTS.map((tmpl) => {
                  const setting = eventSettings.find(
                    (s) => s.eventId === tmpl.id
                  ) ?? { eventId: tmpl.id, isActive: false, flowId: "" };
                  return (
                    <EventCard
                      key={tmpl.id}
                      template={tmpl}
                      setting={setting}
                      connected={connected}
                      onChange={handleEventChange}
                    />
                  );
                })}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* ── AI Skills Tab ─────────────────────────────────────────────────── */}
      {tab === "skills" && (
        <SectionCard
          title="AI Bot Tools"
          description="6 tools activate immediately when Klaviyo is connected — no extra setup required."
        >
          <SkillControlsPanel integration="klaviyo" skills={KLAVIYO_AI_TOOLS} />
        </SectionCard>
      )}

      {/* ── Activity Log Tab ──────────────────────────────────────────────── */}
      {tab === "log" && <IntegrationActivityLog integrationSlug="Klaviyo" />}

      {showWizard && (
        <StarterWorkflowWizard
          integration="klaviyo"
          onClose={() => setShowWizard(false)}
        />
      )}
    </StickyRouteHeader>
  );
}
